import { ICoordinates } from 'react-give-me-map';

export function calculateRectangleCorners(
    center: ICoordinates,
    width: number,
    height: number,
    rotate: number
): ICoordinates[] {
    const earthRadius = 6378137; // Radius of Earth in meters

    // Helper to convert degrees to radians
    const toRadians = (deg: number): number => (deg * Math.PI) / 180;

    // Helper to convert radians to degrees
    // const toDegrees = (rad: number): number => (rad * 180) / Math.PI;

    // Convert lat/lng to Web Mercator projection
    function toWebMercator(lat: number, lng: number): { x: number; y: number } {
        const x = (lng * earthRadius * Math.PI) / 180;
        const y = Math.log(Math.tan(Math.PI / 4 + toRadians(lat) / 2)) * earthRadius;
        return { x, y };
    }

    // Convert Web Mercator back to lat/lng
    function toLatLng(x: number, y: number): ICoordinates {
        const lng = (x / earthRadius) * (180 / Math.PI);
        const lat = (2 * Math.atan(Math.exp(y / earthRadius)) - Math.PI / 2) * (180 / Math.PI);
        return { lat, lng };
    }

    // Rotate a point around a center
    function rotatePoint(
        cx: number,
        cy: number,
        x: number,
        y: number,
        angle: number
    ): { x: number; y: number } {
        const rad = toRadians(angle);
        const dx = x - cx;
        const dy = y - cy;
        const newX = cx + dx * Math.cos(rad) - dy * Math.sin(rad);
        const newY = cy + dx * Math.sin(rad) + dy * Math.cos(rad);
        return { x: newX, y: newY };
    }

    // Convert center to Web Mercator
    const { x: centerX, y: centerY } = toWebMercator(center.lat, center.lng);

    // Half width and height for corner calculations
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Calculate unrotated corners
    const topLeft = { x: centerX - halfWidth, y: centerY + halfHeight };
    const topRight = { x: centerX + halfWidth, y: centerY + halfHeight };
    const bottomLeft = { x: centerX - halfWidth, y: centerY - halfHeight };
    const bottomRight = { x: centerX + halfWidth, y: centerY - halfHeight };

    // Rotate corners around the center
    const rotatedTopLeft = rotatePoint(centerX, centerY, topLeft.x, topLeft.y, rotate);
    const rotatedTopRight = rotatePoint(centerX, centerY, topRight.x, topRight.y, rotate);
    const rotatedBottomLeft = rotatePoint(centerX, centerY, bottomLeft.x, bottomLeft.y, rotate);
    const rotatedBottomRight = rotatePoint(centerX, centerY, bottomRight.x, bottomRight.y, rotate);

    // Convert back to lat/lng
    return [
        toLatLng(rotatedTopLeft.x, rotatedTopLeft.y),
        toLatLng(rotatedTopRight.x, rotatedTopRight.y),
        toLatLng(rotatedBottomRight.x, rotatedBottomRight.y),
        toLatLng(rotatedBottomLeft.x, rotatedBottomLeft.y),
        toLatLng(rotatedTopLeft.x, rotatedTopLeft.y)
    ];
}
